import {
  TNavigationItemElementFields,
  TNavigationItemFields,
  TNavigationItemColumnField,
  TSubMenuElementTypeMapper,
  TSubMenuElementType,
  SecondaryLinkFields,
  TNavigationItemTypeMapper,
  TNavigationItemType,
} from '../types/navigationItem.types';
import { TDictionaryField, TImageField, TLinkField, TStringField } from '../../../types/sitecore.types';

type TLinkOtherProps = { title?: string; target?: string };

export const NavigationItemTypeMapper: TNavigationItemTypeMapper = {
  menu: 'headerMenuDropdownLink',
  link: 'headerMenuButtonLink',
};

export const SubMenuElementTypeMapper: TSubMenuElementTypeMapper = {
  primaryLink: 'headerSubmenuPrimaryBoldLink',
  imageLink: 'headerSubmenuPrimaryImageLink',
  secondaryLink: 'headerSubmenuSecondarySimpleTextLink',
  secondaryLinkWithBold: 'headerSubmenuSecondaryBoldTextLink',
  titleText: 'headerSubmenuTitleText',
  titleLink: 'headerSubmenuTitleLink',
};

export const dictionaryFieldMapper: <T>(field: TDictionaryField<T, T> | undefined) => T | undefined = (field) =>
  field?.fields?.Key?.value || field?.fields?.Phrase?.value;

export class BaseNavigationItemModel {
  protected readonly fields: TNavigationItemFields;

  public readonly type: TNavigationItemType;
  public readonly menuTitle: TStringField;

  constructor(fields: TNavigationItemFields) {
    this.fields = fields;
    this.type = dictionaryFieldMapper(fields?.linkType) || NavigationItemTypeMapper.link;
    this.menuTitle = fields?.menuButton || { value: '' };
  }
}

export class NavigationItemLinkModel extends BaseNavigationItemModel {
  readonly link: TLinkField;
  public readonly id?: string;

  constructor(fields: TNavigationItemFields, id?: string) {
    super(fields);
    this.link = fields?.link || { value: { href: '#', text: '' } };
    this.id = id;
  }

  get linkUrl(): string {
    return this.link?.value?.href ?? '#';
  }
}

export class SubmenuMenuElementModel {
  protected readonly _fields: TNavigationItemElementFields;
  public readonly type: TSubMenuElementType;

  constructor(type: TSubMenuElementType, fields: TNavigationItemElementFields | SecondaryLinkFields) {
    this.type = type;
    this._fields = fields;
  }
}

export class BaseMenuLinkModel extends SubmenuMenuElementModel {
  public readonly link: TLinkField;
  public readonly id?: string;

  constructor(type: TSubMenuElementType, fields: TNavigationItemElementFields | SecondaryLinkFields) {
    super(type, fields);
    this.link = fields?.link || { value: { href: '#' } };
  }

  get linkId(): string {
    return this._fields?.id ?? '';
  }

  get linkUrl(): string {
    return this.link?.value?.href ?? '#';
  }

  get linkText(): string {
    return this.link?.value?.text ?? '';
  }

  get linkOtherProps(): TLinkOtherProps {
    const otherProps: TLinkOtherProps = {};
    if (this.link?.value) {
      const value = this.link?.value;
      if (value?.title) {
        otherProps.title = value?.title;
      }
      if (value?.target) {
        otherProps.target = value?.target;
      }
    }
    return otherProps;
  }
}

export class PrimaryLinkModel extends BaseMenuLinkModel {
  public readonly secondaryLinks: TSubmenuMenuElementModel[];
  public readonly hasSecondaryLinks: boolean;
  public readonly viewAll: TStringField;

  constructor(groupTLinkFields: TNavigationItemElementFields) {
    super(SubMenuElementTypeMapper.primaryLink, groupTLinkFields);
    if (groupTLinkFields?.secondaryLinks && Array.isArray(groupTLinkFields.secondaryLinks) && groupTLinkFields.secondaryLinks.length) {
      this.hasSecondaryLinks = true;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      this.secondaryLinks = groupTLinkFields.secondaryLinks?.map((secondaryLink) => newSubMenuElement(secondaryLink.fields));
    } else {
      this.secondaryLinks = [];
      this.hasSecondaryLinks = false;
    }
    this.viewAll = groupTLinkFields?.viewAll || { value: '' };
  }

  get viewAllText(): string {
    return this.viewAll?.value ?? '';
  }

  get hasViewAll(): boolean {
    return this.viewAllText !== '';
  }
}

export class SecondaryLinkModel extends BaseMenuLinkModel {
  constructor(fields: SecondaryLinkFields) {
    super(SubMenuElementTypeMapper.secondaryLink, fields);
  }
}

export class ImageLinkModel extends BaseMenuLinkModel {
  public readonly image: TImageField;
  public readonly hideOnMobile: boolean | undefined;

  constructor(fields: TNavigationItemElementFields) {
    super(SubMenuElementTypeMapper.imageLink, fields);

    this.image = fields?.image || { value: { src: '' } };
    this.hideOnMobile = fields?.hideOnMobile?.value;
  }
}

export class SecondaryLinkWithBoldModel extends BaseMenuLinkModel {
  public readonly divider: TStringField;
  public readonly boldText: TStringField;

  constructor(fields: SecondaryLinkFields) {
    super(SubMenuElementTypeMapper.secondaryLinkWithBold, fields);

    this.divider = fields?.divider ?? { value: '' };
    this.boldText = fields?.boldSecondaryText ?? { value: '' };
  }
}

export class TitleLinkModel extends BaseMenuLinkModel {
  constructor(fields: TNavigationItemElementFields) {
    super(SubMenuElementTypeMapper.titleLink, fields);
  }
}

export class TitleTextModel extends SubmenuMenuElementModel {
  public readonly title: TStringField;
  constructor(fields: TNavigationItemElementFields) {
    super(SubMenuElementTypeMapper.titleText, fields);
    this.title = this._fields?.title ?? { value: '' };
  }
}

export const newSubMenuElement = (elementFields: TNavigationItemElementFields): TSubmenuMenuElementModel => {
  if (elementFields?.link?.value?.href === '' && elementFields?.title) return new TitleTextModel(elementFields);
  switch (dictionaryFieldMapper(elementFields?.linkType)) {
    case SubMenuElementTypeMapper.primaryLink:
      return new PrimaryLinkModel(elementFields);
    case SubMenuElementTypeMapper.secondaryLink:
      return new SecondaryLinkModel(elementFields);
    case SubMenuElementTypeMapper.imageLink:
      return new ImageLinkModel(elementFields);
    case SubMenuElementTypeMapper.secondaryLinkWithBold:
      return new SecondaryLinkWithBoldModel(elementFields);
    case SubMenuElementTypeMapper.titleText:
      return new TitleTextModel(elementFields);
    case SubMenuElementTypeMapper.titleLink:
      return new TitleLinkModel(elementFields);
    default:
      return new PrimaryLinkModel(elementFields);
  }
};

export class NavigationItemColumnModel {
  private readonly sitecoreElements: TNavigationItemColumnField;
  public readonly elements: TSubmenuMenuElementModel[];
  public readonly id?: string;

  constructor(sitecoreElements: TNavigationItemColumnField, id?: string) {
    this.sitecoreElements = sitecoreElements;
    this.id = id;

    if (this.sitecoreElements && Array.isArray(this.sitecoreElements)) {
      this.elements = this.sitecoreElements?.map((element) => newSubMenuElement(element.fields));
    } else {
      this.elements = [];
    }
  }
}

export class NavigationItemMenuModel extends BaseNavigationItemModel {
  public readonly columns: [
    NavigationItemColumnModel,
    NavigationItemColumnModel,
    NavigationItemColumnModel,
    NavigationItemColumnModel,
    NavigationItemColumnModel
  ];
  public readonly isLastColumnGray: boolean;
  public readonly type: TNavigationItemType;
  public readonly displayName: string;
  public readonly id?: string;


  constructor(fields: TNavigationItemFields, id: string, displayName?: string) {
    super(fields);
    this.columns = [
      new NavigationItemColumnModel(fields?.section1, id),
      new NavigationItemColumnModel(fields?.section2, id),
      new NavigationItemColumnModel(fields?.section3, id),
      new NavigationItemColumnModel(fields?.section4, id),
      new NavigationItemColumnModel(fields?.section5, id),
    ];
    this.isLastColumnGray = fields?.isSection4Gray?.value ?? false;
    this.displayName = displayName || '';
    this.id = id;
  }
}

export type TNavigationItemModel = NavigationItemLinkModel | NavigationItemMenuModel;

export const newNavigationItemModel = (fields: TNavigationItemFields, id: string, displayName?: string): TNavigationItemModel => {
  switch (dictionaryFieldMapper(fields?.linkType)) {
    case NavigationItemTypeMapper.link:
      return new NavigationItemLinkModel(fields, id);
    case NavigationItemTypeMapper.menu:
      return new NavigationItemMenuModel(fields, id, displayName);
    default:
      return new NavigationItemLinkModel(fields, id);
  }
};

export type TSubmenuMenuElementModel =
  | PrimaryLinkModel
  | SecondaryLinkModel
  | SecondaryLinkWithBoldModel
  | ImageLinkModel
  | TitleTextModel
  | TitleLinkModel;
